import { isTransientRequestError } from "../../services/networkErrors";

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export function electricalSaveFailure(error, greek = false) {
  const tr = (el, en) => greek ? el : en;
  const retryable = isTransientRequestError(error);
  const status = Number(error?.status);
  const message = status === 401 ? tr(
    "Η σύνδεσή σας έληξε ή δεν είναι διαθέσιμη. Συνδεθείτε ξανά και αποθηκεύστε το σχέδιο.",
    "Your session has expired or is unavailable. Sign in again and save the design.")
    : status === 403 ? tr(
      "Δεν έχετε δικαίωμα για αυτή την ενέργεια. Ζητήστε πρόσβαση από τον διαχειριστή πριν αποθηκεύσετε ξανά το σχέδιο.",
      "You do not have permission for this action. Ask your administrator for access before saving the design again.")
    : status === 404 ? tr(
      "Το επιλεγμένο πλαίσιο ή ο αντιστροφέας δεν είναι διαθέσιμος στον κατάλογό σας. Επιλέξτε διαθέσιμο εξοπλισμό και αποθηκεύστε ξανά το σχέδιο.",
      "The selected module or inverter is unavailable in your catalogue. Select available equipment and save the design again.")
    : [400, 422].includes(status) ? tr(
      "Τα στοιχεία του σχεδίου δεν έγιναν αποδεκτά. Ελέγξτε τον εξοπλισμό και τις ηλεκτρολογικές ρυθμίσεις και αποθηκεύστε ξανά.",
      "The design data was not accepted. Review the equipment and electrical settings, then save again.")
    : retryable ? tr(
      "Προσωρινό πρόβλημα επικοινωνίας ή διαθεσιμότητας του διακομιστή. Η αποθήκευση δεν ολοκληρώθηκε.",
      "Temporary connection or server availability problem. The save did not complete.")
    : error?.userMessage || tr(
      "Ο έλεγχος ή η αποθήκευση δεν ολοκληρώθηκε. Δοκιμάστε ξανά με «Αποθήκευση σχεδίου». Αν το πρόβλημα επιμένει, επικοινωνήστε με την υποστήριξη.",
      "The check or save did not complete. Try Save design again. If the problem persists, contact support.");
  return { status: "error", retryable, message,
    ...(Number.isFinite(error?.retryAfterMs) ? { retryAfterMs: error.retryAfterMs } : {}) };
}

// These results can arrive while the electrical save awaits catalogue data or
// string mapping. They do not change the electrical design being validated.
const horizonKeys = ["horizonProfile", "horizonProfileSite"];
function inputs(config, derivedCapacity) {
  const result = { ...config, panelOrientation: { ...config?.panelOrientation } };
  delete result.orientationOptimization;
  horizonKeys.forEach(key => delete result.panelOrientation[key]);
  if (derivedCapacity) {
    result.pvInstalledCap = { ...config?.pvInstalledCap };
    delete result.pvInstalledCap.kw;
  }
  return result;
}

export function electricalSaveError(message) {
  return Object.assign(new Error(message), { userMessage: message });
}

export function electricalSaveConflict(greek) {
  return electricalSaveError(greek
    ? "Τα στοιχεία σχεδιασμού άλλαξαν κατά την αποθήκευση. Ελέγξτε το σχέδιο και αποθηκεύστε ξανά."
    : "Design inputs changed while saving. Review the design and save again.");
}

export function mergeElectricalSaveSnapshot(before, proposed, latest, greek = false) {
  // With an existing design the capacity field is derived from its modules.
  // The first auto-design can also update it to exactly the capacity being
  // saved. Any other change to an unsaved project's target stays protected.
  const proposedKw = Number(proposed?.pvInstalledCap?.kw);
  const derivedCapacity = Boolean(before?.systemDesign && Object.keys(before.systemDesign).length) ||
    (Number.isFinite(proposedKw) && proposedKw > 0 && Number(latest?.pvInstalledCap?.kw) === proposedKw);
  if (!same(inputs(before, derivedCapacity), inputs(latest, derivedCapacity))) {
    throw electricalSaveConflict(greek);
  }
  const merged = { ...proposed };
  const copyLatest = (target, source, key) => {
    if (Object.prototype.hasOwnProperty.call(source || {}, key)) target[key] = source[key];
    else delete target[key];
  };
  if (!same(before?.orientationOptimization, latest?.orientationOptimization)) {
    copyLatest(merged, latest, "orientationOptimization");
  }
  horizonKeys.forEach(key => {
    if (!same(before?.panelOrientation?.[key], latest?.panelOrientation?.[key])) {
      merged.panelOrientation = { ...merged.panelOrientation };
      copyLatest(merged.panelOrientation, latest?.panelOrientation, key);
    }
  });
  return merged;
}
